import { MessagePathRecord } from "@/shared/domain/topics";

import { type Range, MessageFilter } from "../MessageFilter";

import type { ReaderLike, RowGroup } from "./ReaderLike";
import { toTimestamp } from "./transform";

export async function convertLogtimeFilterToRowIndexFilter(
  filter: MessageFilter<bigint>,
  reader: ReaderLike,
  timestamp: { column: string; messagePath: MessagePathRecord },
): Promise<MessageFilter<number>> {
  // Collapse all the data points that match the timestamp filters into a collection
  // of row index range filters.
  const params: Range<number>[] = [];
  /** Tracks whether the ingested timestamps are ordered to help debug errors. */
  let hasUnorderedTimestamps = false;
  for await (const chunk of reader.iterColumnChunks({
    columns: [timestamp.column],
    rowGroupFilter: (rowGroup: RowGroup) =>
      rowGroupColumnChunkMatchesLogtimeFilter(rowGroup, timestamp, filter),
  })) {
    if (chunk.columnName !== timestamp.column) {
      continue;
    }
    let lastTimestamp: bigint | undefined;
    for (const [index, row] of chunk.columnData.entries()) {
      const timestampValue = toTimestamp(row, timestamp.messagePath);
      if (lastTimestamp !== undefined && timestampValue < lastTimestamp) {
        hasUnorderedTimestamps = true;
      }
      if (filter.matches(timestampValue)) {
        const dataPageAdjustedIndex = index + chunk.rowStart;
        const lastRange = params.at(-1) ?? {
          min: dataPageAdjustedIndex,
          max: dataPageAdjustedIndex,
        };
        const lastRangeUpperBound = lastRange?.max ?? dataPageAdjustedIndex;
        // If lastRange took the default value, dataPageAdjustedIndex - lastRangeUpperBound === 0
        // and we will fall through to the else statement and push a new filter param to the list.
        if (dataPageAdjustedIndex - lastRangeUpperBound === 1) {
          // extend the range if this index is contiguous with the last range
          lastRange.max = dataPageAdjustedIndex;
        } else {
          // create a new range of length 1
          params.push({
            min: dataPageAdjustedIndex,
            max: dataPageAdjustedIndex,
          });
        }
      }
      lastTimestamp = timestampValue;
    }
  }

  if (params.length === 0) {
    if (hasUnorderedTimestamps) {
      throw new Error(
        `The column '${timestamp.column}' used as this topic's ` +
          "source of timestamps is not in ascending order. " +
          "This prevents selecting ranges of data by time. " +
          `Please rewrite and re-order the data by '${timestamp.column}' ASC ` +
          "and rerun ingestion, or rerun ingestion on the data as-is " +
          "using a different timestamp column.",
      );
    }
    // No rows in this file fall within the requested logTime range. This is a
    // legitimate, non-error outcome: when several files/topics are plotted on a
    // single panel, a zoom or pan can request a time window that lies entirely
    // outside one file's data (or row-group statistics prune every group).
    // Return a filter that matches no rows so the caller yields no data for this
    // file, rather than failing the whole panel.
    return MessageFilter.fromList<number>([]);
  }

  if (params.length === 1) {
    return MessageFilter.fromRange(params[0]);
  }

  return MessageFilter.combine(params.map(MessageFilter.fromRange));
}

export function matchesFilter(
  bounds: { rowStart: number; rowEnd: number },
  filter: MessageFilter<number>,
) {
  const { rowStart, rowEnd } = bounds;
  if (filter.isRangeFilter()) {
    const { min = rowStart, max = rowEnd } = filter;
    return min <= rowEnd && max >= rowStart;
  }

  if (filter.isListFilter()) {
    return filter.matches(new Set([rowStart, rowEnd]));
  }

  if (filter.isCombinedFilter()) {
    for (const subfilter of filter.filters) {
      if (matchesFilter(bounds, subfilter)) {
        return true;
      }
    }
    return false;
  }
  return false;
}

function rowGroupColumnChunkMatchesLogtimeFilter(
  rowGroup: RowGroup,
  timestamp: { column: string; messagePath: MessagePathRecord },
  filter: MessageFilter<bigint>,
) {
  const stats =
    rowGroup.columns.find(
      (col) => col.meta_data?.path_in_schema.join(".") === timestamp.column,
    )?.meta_data?.statistics ?? {};

  if (stats.min_value === undefined || stats.max_value === undefined) {
    return true;
  }
  const minValue = toTimestamp(stats.min_value, timestamp.messagePath);
  const maxValue = toTimestamp(stats.max_value, timestamp.messagePath);

  if (filter.isRangeFilter()) {
    const filterStart = filter.min ?? minValue;
    const filterEnd = filter.max ?? maxValue;
    return filterStart <= maxValue && filterEnd >= minValue;
  }

  if (filter.isListFilter()) {
    // Check if value in the filter falls within this group's range
    return filter.matching.some(
      (value) => value >= minValue && value <= maxValue,
    );
  }

  if (filter.isCombinedFilter()) {
    // For combined filters, the group must contain data matching ALL filters
    for (const subfilter of filter.filters) {
      if (
        !rowGroupColumnChunkMatchesLogtimeFilter(rowGroup, timestamp, subfilter)
      ) {
        return false;
      }
    }
    return true;
  }

  return false;
}
